import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { customFetcher } from './customFetcher';
import { INotificationListResponse, notificationListResponseSchema } from '../Interfaces/INotification';

interface UseNotificationsOptions {
    limit?: number;
    unreadOnly?: boolean;
    enabled?: boolean;
}

export const notificationsQueryKey = (limit: number, unreadOnly: boolean) =>
    ['notifications', limit, unreadOnly ? 'unread' : 'all'] as const;

export const useNotifications = ({ limit = 20, unreadOnly = false, enabled = true }: UseNotificationsOptions = {}) =>
    useQuery<INotificationListResponse, Error>({
        queryKey: notificationsQueryKey(limit, unreadOnly),
        refetchOnWindowFocus: true,
        // Polling statt SSE (siehe notificationsapp/views.py): 30s-Intervall als
        // Kompromiss aus Aktualität und Last.
        refetchInterval: 30000,
        enabled,
        queryFn: async () => {
            const query = new URLSearchParams({
                limit: String(limit),
            });
            if (unreadOnly) {
                query.set('unread_only', '1');
            }
            const data = await customFetcher<unknown>({ endpoint: `/notifications/?${query.toString()}` });
            return notificationListResponseSchema.parse(data);
        },
        initialData: { items: [], unread_count: 0 },
    });

export const useMarkNotificationRead = () => {
    const queryClient = useQueryClient();
    return useMutation<{ id: number; is_read: boolean }, Error, { id: number }>({
        mutationFn: async ({ id }) => customFetcher({
            endpoint: `/notifications/${id}/read/`,
            method: 'POST',
        }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });
};

export const useMarkAllNotificationsRead = () => {
    const queryClient = useQueryClient();
    return useMutation<{ success: boolean }, Error, void>({
        mutationFn: async () => customFetcher({
            endpoint: '/notifications/mark-all-read/',
            method: 'POST',
        }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });
};
